import { publicRuntimeValue } from "@matrix-idp/config";

const SESSION_KEY = "matrix-ops-oauth-session";
const TRANSACTION_KEY = "matrix-ops-oauth-transaction";
const CLOCK_SKEW_SECONDS = 30;
const DEFAULT_RETURN_TO = "/documents";

interface OAuthSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface OAuthTransaction {
  state: string;
  verifier: string;
  returnTo: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

export class OpsOAuthError extends Error {
  constructor(
    readonly retryable: boolean,
    message = "OAuth token request failed"
  ) {
    super(message);
    this.name = "OpsOAuthError";
  }
}

let loginCompletion: { callbackUrl: string; promise: Promise<string> } | null = null;
let refreshCompletion: Promise<string | null> | null = null;
const sessionClearedListeners = new Set<() => void>();

export function canonicalOpsConsoleUrl(currentUrl = window.location.href): string | null {
  const configuredRedirectUri = publicRuntimeValue("VITE_OPS_OAUTH_REDIRECT_URI", import.meta.env);
  if (!configuredRedirectUri) return null;

  const current = new URL(currentUrl);
  const redirect = new URL(configuredRedirectUri);
  if (current.origin === redirect.origin) return null;

  return new URL(`${current.pathname}${current.search}${current.hash}`, redirect.origin).toString();
}

export async function beginOpsLogin(returnTo = window.location.pathname): Promise<void> {
  window.location.assign(await prepareOpsLogin(returnTo));
}

export async function beginOpsReauthentication(returnTo = window.location.pathname): Promise<void> {
  const authorizationUrl = await prepareOpsLogin(returnTo);
  const logoutUrl = new URL("/logout", oauthConfig().portalUrl);
  logoutUrl.searchParams.set("continue_to", authorizationUrl);
  window.location.replace(logoutUrl);
}

async function prepareOpsLogin(returnTo: string): Promise<string> {
  const config = oauthConfig();
  const verifier = randomBase64Url(64);
  const state = randomBase64Url(32);
  const transaction: OAuthTransaction = {
    state,
    verifier,
    returnTo: safeReturnTo(returnTo)
  };
  loginCompletion = null;
  sessionStorage.setItem(TRANSACTION_KEY, JSON.stringify(transaction));
  const challenge = base64Url(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))
  );
  const query = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: "code",
    code_challenge: challenge,
    code_challenge_method: "S256",
    scope: "openid profile email",
    state
  });
  return `${config.authUrl}/oauth/authorize?${query.toString()}`;
}

export function completeOpsLogin(callbackUrl = window.location.href): Promise<string> {
  if (loginCompletion?.callbackUrl === callbackUrl) return loginCompletion.promise;
  const promise = completeOpsLoginOnce(callbackUrl);
  loginCompletion = { callbackUrl, promise };
  void promise.catch(() => {
    if (loginCompletion?.promise === promise) loginCompletion = null;
  });
  return promise;
}

export function hasPendingOpsLogin(): boolean {
  return readTransaction() !== null;
}

async function completeOpsLoginOnce(callbackUrl: string): Promise<string> {
  const url = new URL(callbackUrl);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  const error = url.searchParams.get("error");
  const transaction = readTransaction();
  if (error) {
    clearPendingOpsLogin();
    throw new Error(`OAuth authorization failed: ${error}`);
  }
  if (!code || !state || !transaction || state !== transaction.state) {
    clearPendingOpsLogin();
    throw new Error("OAuth callback state is invalid");
  }
  try {
    const config = oauthConfig();
    const token = await exchangeToken({
      grant_type: "authorization_code",
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      code,
      code_verifier: transaction.verifier
    });
    clearPendingOpsLogin();
    storeToken(token);
    return transaction.returnTo;
  } catch (error) {
    if (!(error instanceof OpsOAuthError && error.retryable)) clearPendingOpsLogin();
    throw error;
  }
}

export async function getOpsAccessToken(): Promise<string | null> {
  const session = readSession();
  if (!session) return null;
  if (session.expiresAt > Date.now() / 1000 + CLOCK_SKEW_SECONDS) return session.accessToken;
  if (!refreshCompletion) {
    refreshCompletion = refreshOpsSession(session).finally(() => {
      refreshCompletion = null;
    });
  }
  return refreshCompletion;
}

async function refreshOpsSession(session: OAuthSession): Promise<string | null> {
  try {
    const config = oauthConfig();
    const token = await exchangeToken({
      grant_type: "refresh_token",
      client_id: config.clientId,
      refresh_token: session.refreshToken
    });
    const current = readSession();
    if (!current || current.refreshToken !== session.refreshToken) {
      return current?.accessToken ?? null;
    }
    storeToken(token, session.refreshToken);
    return token.access_token;
  } catch (error) {
    if (error instanceof OpsOAuthError && error.retryable) throw error;
    const current = readSession();
    if (current && current.refreshToken !== session.refreshToken) {
      return current.accessToken;
    }
    clearOpsSession();
    return null;
  }
}

export function clearOpsSession(): void {
  const hadSession = window.localStorage.getItem(SESSION_KEY) !== null;
  loginCompletion = null;
  refreshCompletion = null;
  window.localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(TRANSACTION_KEY);
  if (hadSession) {
    for (const listener of sessionClearedListeners) listener();
  }
}

export function onOpsSessionCleared(listener: () => void): () => void {
  sessionClearedListeners.add(listener);
  return () => sessionClearedListeners.delete(listener);
}

export async function signOutOps(): Promise<void> {
  const session = readSession();
  try {
    if (!session) return;
    const config = oauthConfig();
    const response = await fetch(`${config.authUrl}/logout?scope=local`, {
      method: "POST",
      headers: {
        apikey: config.publishableKey,
        authorization: `Bearer ${session.accessToken}`
      },
      credentials: "omit"
    });
    if (!response.ok) throw new Error("OAuth logout failed");
  } finally {
    clearOpsSession();
  }
}

export function hasOpsSession(): boolean {
  return readSession() !== null;
}

async function exchangeToken(body: Record<string, string>): Promise<TokenResponse> {
  const config = oauthConfig();
  let response: Response;
  try {
    response = await fetch(`${config.authUrl}/oauth/token`, {
      method: "POST",
      headers: {
        accept: "application/json",
        apikey: config.publishableKey,
        "content-type": "application/x-www-form-urlencoded"
      },
      body: new URLSearchParams(body),
      credentials: "omit"
    });
  } catch {
    throw new OpsOAuthError(true, "OAuth token endpoint is unavailable");
  }
  const value: unknown = await response.json().catch(() => null);
  if (response.ok && isTokenResponse(value)) return value;
  if (response.status >= 500 || response.status === 408 || response.status === 429) {
    throw new OpsOAuthError(true);
  }
  throw new OpsOAuthError(false);
}

function storeToken(token: TokenResponse, previousRefreshToken?: string): void {
  const refreshToken = token.refresh_token ?? previousRefreshToken;
  if (!refreshToken) throw new Error("OAuth server did not issue a refresh token");
  const session: OAuthSession = {
    accessToken: token.access_token,
    refreshToken,
    expiresAt: Math.floor(Date.now() / 1000) + token.expires_in
  };
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function readSession(): OAuthSession | null {
  const value = parseJson<OAuthSession>(window.localStorage.getItem(SESSION_KEY));
  return value && isOAuthSession(value) ? value : null;
}

function readTransaction(): OAuthTransaction | null {
  const value = parseJson<OAuthTransaction>(sessionStorage.getItem(TRANSACTION_KEY));
  return value && isOAuthTransaction(value) ? value : null;
}

function clearPendingOpsLogin(): void {
  sessionStorage.removeItem(TRANSACTION_KEY);
}

function oauthConfig() {
  const supabaseUrl = publicRuntimeValue("VITE_SUPABASE_URL", import.meta.env)?.replace(/\/$/, "");
  const clientId = publicRuntimeValue("VITE_OPS_OAUTH_CLIENT_ID", import.meta.env);
  const publishableKey = publicRuntimeValue("VITE_SUPABASE_PUBLISHABLE_KEY", import.meta.env);
  const portalUrl = publicRuntimeValue("VITE_AUTH_PORTAL_URL", import.meta.env)?.replace(
    /\/$/u,
    ""
  );
  if (!supabaseUrl || !clientId || !publishableKey || !portalUrl) {
    throw new Error(
      "VITE_SUPABASE_URL, VITE_AUTH_PORTAL_URL, VITE_SUPABASE_PUBLISHABLE_KEY, and VITE_OPS_OAUTH_CLIENT_ID are required"
    );
  }
  return {
    authUrl: `${supabaseUrl}/auth/v1`,
    clientId,
    portalUrl,
    publishableKey,
    redirectUri:
      publicRuntimeValue("VITE_OPS_OAUTH_REDIRECT_URI", import.meta.env) ||
      `${window.location.origin}/oauth/callback`
  };
}

function randomBase64Url(bytes: number): string {
  const value = new Uint8Array(bytes);
  crypto.getRandomValues(value);
  return base64Url(value.buffer);
}

function base64Url(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function safeReturnTo(value: string): string {
  const url = new URL(value, window.location.origin);
  if (url.origin !== window.location.origin || url.pathname === "/oauth/callback") {
    return DEFAULT_RETURN_TO;
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

function parseJson<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function isOAuthSession(value: OAuthSession): boolean {
  return (
    typeof value.accessToken === "string" &&
    value.accessToken.length > 0 &&
    typeof value.refreshToken === "string" &&
    value.refreshToken.length > 0 &&
    Number.isSafeInteger(value.expiresAt)
  );
}

function isOAuthTransaction(value: OAuthTransaction): boolean {
  return (
    typeof value.state === "string" &&
    value.state.length > 0 &&
    typeof value.verifier === "string" &&
    value.verifier.length > 0 &&
    typeof value.returnTo === "string"
  );
}

function isTokenResponse(value: unknown): value is TokenResponse {
  if (!value || typeof value !== "object") return false;
  const token = value as Record<string, unknown>;
  return (
    typeof token.access_token === "string" &&
    token.access_token.length > 0 &&
    typeof token.expires_in === "number" &&
    Number.isSafeInteger(token.expires_in) &&
    token.expires_in > 0 &&
    typeof token.token_type === "string" &&
    token.token_type.toLowerCase() === "bearer"
  );
}
